import { z } from 'zod';

const FundAdditionalIncomeAndExpenditureBaseModel = z.object({
  id: z.string().uuid(),
  projectId: z.string().nullable(), // NOTE: 過去KSシート移行で事業を参照せずに作成するデータがあるためnullableとしている
  beginningOfTheFundBalance: z.number().nullish(),
  beginningOfTheEstimatedFundBalanceNextYear: z.number().nullish(),
  grantedAmountByTheCountry: z.number().nullish(),
  estimatedGrantedAmountByTheCountry: z.number().nullish(),
  estimatedGrantedAmountByTheCountryNextYear: z.number().nullish(),
  investmentIncome: z.number().nullish(),
  estimatedInvestmentIncome: z.number().nullish(),
  estimatedInvestmentIncomeNextYear: z.number().nullish(),
  nationalEquivalentOfInvestmentIncome: z.number().nullish(),
  estimatedNationalEquivalentOfInvestmentIncome: z.number().nullish(),
  estimatedNationalEquivalentOfInvestmentIncomeNextYear: z.number().nullish(),
  businessIncome: z.number().nullish(),
  estimatedBusinessIncome: z.number().nullish(),
  estimatedBusinessIncomeNextYear: z.number().nullish(),
  nationalEquivalentOfBusinessIncome: z.number().nullish(),
  estimatedNationalEquivalentOfBusinessIncome: z.number().nullish(),
  estimatedNationalEquivalentOfBusinessIncomeNextYear: z.number().nullish(),
  otherIncomeAmount: z.number().nullish(),
  estimatedOtherIncomeAmount: z.number().nullish(),
  estimatedOtherIncomeAmountNextYear: z.number().nullish(),
  businessExpense: z.number(),
  estimatedBusinessExpense: z.number(),
  estimatedBusinessExpenseNextYear: z.number(),
  administrativeExpense: z.number().nullish(),
  estimatedAdministrativeExpense: z.number().nullish(),
  estimatedAdministrativeExpenseNextYear: z.number().nullish(),
  choreExpensesOfFundEstablishing: z.number().nullish(),
  estimatedChoreExpensesOfFundEstablishing: z.number().nullish(),
  estimatedChoreExpensesOfFundEstablishingNextYear: z.number().nullish(),
  personnelExpensesOfFundEstablishing: z.number().nullish(),
  estimatedPersonnelExpensesOfFundEstablishing: z.number().nullish(),
  estimatedPersonnelExpensesOfFundEstablishingNextYear: z.number().nullish(),
  amountReturnedToTheNationalTreasury: z.number().nullish(),
  estimatedAmountReturnedToTheNationalTreasury: z.number().nullish(),
  estimatedAmountReturnedToTheNationalTreasuryNextYear: z.number().nullish(),
  amountReturnedToTheOrganization: z.number().nullish(),
  estimatedAmountReturnedToTheOrganization: z.number().nullish(),
  estimatedAmountReturnedToTheOrganizationNextYear: z.number().nullish(),
  nationalEquivalentOfTheBalance: z.number().nullish(),
  estimatedNationalEquivalentOfTheBalance: z.number().nullish(),
  estimatedNationalEquivalentOfTheBalanceNextYear: z.number().nullish(),
  choreExpensesNotFromThisFund: z.number().nullish(),
  estimatedChoreExpensesNotFromThisFund: z.number().nullish(),
  estimatedChoreExpensesNotFromThisFundNextYear: z.number().nullish(),
  personnelExpensesNotFromThisFund: z.number().nullish(),
  estimatedPersonnelExpensesNotFromThisFund: z.number().nullish(),
  estimatedPersonnelExpensesNotFromThisFundNextYear: z.number().nullish(),
});

export const FundAdditionalIncomeAndExpenditureSchema =
  FundAdditionalIncomeAndExpenditureBaseModel.omit({
    id: true,
    projectId: true,
  }).extend({
    prior: FundAdditionalIncomeAndExpenditureBaseModel.nullish(),
  });

export const FundAdditionalIncomeAndExpenditurePriorMutationSchema =
  FundAdditionalIncomeAndExpenditureBaseModel.omit({
    projectId: true,
  });

export type FundAdditionalIncomeAndExpenditureType = z.infer<
  typeof FundAdditionalIncomeAndExpenditureBaseModel
> & {
  oneYearAgoDivergence?: {
    divergenceAmount: number | null;
    estimatedBusinessExpense: number;
    businessExpense: number;
    divergenceRate: number | null;
  } | null;
  twoYearsAgoDivergence?: {
    divergenceAmount: number | null;
    estimatedBusinessExpense: number;
    businessExpense: number;
    divergenceRate: number | null;
  } | null;
  prior?: z.infer<typeof FundAdditionalIncomeAndExpenditureBaseModel> | null;
};
export const FundAdditionalIncomeAndExpenditureModel: z.ZodType<FundAdditionalIncomeAndExpenditureType> =
  FundAdditionalIncomeAndExpenditureBaseModel.extend({
    oneYearAgoDivergence: z
      .object({
        divergenceAmount: z.number().nullable(),
        estimatedBusinessExpense: z.number(),
        businessExpense: z.number(),
        divergenceRate: z.number().nullable(),
      })
      .nullish(),
    twoYearsAgoDivergence: z
      .object({
        divergenceAmount: z.number().nullable(),
        estimatedBusinessExpense: z.number(),
        businessExpense: z.number(),
        divergenceRate: z.number().nullable(),
      })
      .nullish(),
    prior: z.lazy(() => FundAdditionalIncomeAndExpenditureModel.nullish()),
  });

const periodScope = z.enum([
  'previous-year-performance',
  'estimated-this-year',
  'estimated-next-year',
]);

export type PeriodScopeType = z.infer<typeof periodScope>;
